import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardHeader, Button, Collapse, CardBody } from 'reactstrap';
import { GetMeitnerSchoolUnits, MeitnerExportState } from '../../../../../../store/MeitnerExportAction';
import { CoreMeitnerExportConfiguration, CoreMeitnerExportSchoolUnit, CoreMeitnerExportSchoolUnitMap, MeitnerSchoolUnit, MeitnerSchoolUnitResponse } from '../../../../../../models/CoreMeitnerModels';
import { useDispatch, useSelector } from 'react-redux';
import { RequestState } from '../../../../../../models/RequestState';
import StatusCode from '../../../../../../util/StatusCode';
import MultiSelectInput from '../../../../../misc/MultiSelectInput';
import { getErrorObject } from '../../../../../../models/ErrorObject';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CoreExportModuleSchoolUnit } from '../../../../../../models/CoreModels';


interface MeitnerExportSchoolUnitsState {
    meitnerExport: MeitnerExportState
}

interface MeitnerExportSchoolUnitsProps {
    disabled: boolean
    configuration: CoreMeitnerExportConfiguration
    exportSchoolUnits: CoreExportModuleSchoolUnit[]
    updateValue: (x: CoreMeitnerExportSchoolUnit[]) => void
}

const MeitnerExportSchoolUnits = (props: MeitnerExportSchoolUnitsProps) => {
    const { t } = useTranslation(['configuration']);
    const [isOpen, setIsOpen] = useState<boolean>(true);
    const dispatch = useDispatch();

    const meitnerSchoolUnits = useSelector<MeitnerExportSchoolUnitsState, RequestState<MeitnerSchoolUnitResponse>>(state => state.meitnerExport.meitnerSchoolUnits)

    useEffect(() => {
        if (props.configuration.clientId && props.configuration.clientSecret) {
            GetMeitnerSchoolUnits(props.configuration.clientId, props.configuration.clientSecret, props.configuration.environment)(dispatch);
        }
    }, [dispatch, props.configuration.clientId, props.configuration.clientSecret, props.configuration.environment]);

    const onChangeFlowSyncSchoolUnit = (value: string[] | null | undefined, meitnerSchoolUnit: MeitnerSchoolUnit) => {
        let updateSchoolUnits = [...props.configuration.meitnerSchoolUnits];
        let schoolUnitMaps: CoreMeitnerExportSchoolUnitMap[] = [];

        const existing = updateSchoolUnits.findIndex(x => x.meitnerSchoolUnitId === meitnerSchoolUnit.id);

        //Update schoolUnitMaps
        if (value) {
            value.map(x => {
                schoolUnitMaps.push({
                    meitnerExportSchoolUnitId: updateSchoolUnits[existing]?.id,
                    flowSyncSchoolUnitId: x
                })
            })
        } else {
            schoolUnitMaps = [];
        }

        //If the schoolUnit is new and there is no schoolUnitMaps
        if (!value?.length && updateSchoolUnits[existing].id === undefined) {
            updateSchoolUnits.splice(existing, 1);
        } else {
            //Add schoolUnit and schoolUnitMaps
            const addSchoolUnit = {
                id: updateSchoolUnits[existing]?.id,
                meitnerSchoolUnitId: meitnerSchoolUnit.id,
                meitnerExportId: props.configuration.id,
                displayName: meitnerSchoolUnit.title,
                meitnerExportSchoolUnitMaps: schoolUnitMaps
            };
            if (existing !== -1) {
                updateSchoolUnits.splice(existing, 1, addSchoolUnit)
            } else {
                updateSchoolUnits.push(addSchoolUnit)
            }
        }

        props.updateValue(updateSchoolUnits);
    }

    const renderMeitnerSchoolUnits = () => {
        if (meitnerSchoolUnits.code === StatusCode.COMPLETE && (props.configuration.clientId && props.configuration.clientSecret)) {
            return (meitnerSchoolUnits.data.items.map(x => renderRow(x)))
        };
        if ((meitnerSchoolUnits.code === StatusCode.ERROR) || (!props.configuration.clientId || !props.configuration.clientSecret)) {
            return (
                props.configuration.meitnerSchoolUnits
                    .filter((value, index, self) => self.map(x => x.meitnerSchoolUnitId).indexOf(value.meitnerSchoolUnitId) === index)
                    .map(msu => {
                        return renderRow({
                            id: msu.meitnerSchoolUnitId,
                            title: msu.displayName,
                        })
                    })
            );
        };
    };

    const getSelectedFlowSyncSchoolUnit = (meitnerSchoolUnit: MeitnerSchoolUnit) => {
        const schoolUnitMaps = props.configuration.meitnerSchoolUnits.find(y => y.meitnerSchoolUnitId === meitnerSchoolUnit.id)?.meitnerExportSchoolUnitMaps;

        if (schoolUnitMaps) {
            return schoolUnitMaps.map(s => s.flowSyncSchoolUnitId)
        } else {
            return [];
        };
    };

    const filterExportSchoolUnits = (exportSchoolUnit: CoreExportModuleSchoolUnit, meitnerId: string) => {
        //Remove schoolunit if title is undefined
        if (!exportSchoolUnit.title) {
            return false;
        }

        //Show selected schools
        if (props.configuration.meitnerSchoolUnits.find(x => x.meitnerSchoolUnitId === meitnerId && x.meitnerExportSchoolUnitMaps?.find(y => y.flowSyncSchoolUnitId === exportSchoolUnit.id))) {
            return true;
        }

        //Remove already selected schools from select
        if (props.configuration.meitnerSchoolUnits.flatMap(x => x.meitnerExportSchoolUnitMaps ?? []).some(y => y.flowSyncSchoolUnitId === exportSchoolUnit.id)) {
            return false;
        };

        return exportSchoolUnit.isActive;
    };

    const renderRow = (meitnerSchoolUnit: MeitnerSchoolUnit) => {
        return (<>
            <div key={meitnerSchoolUnit.id} className="mb-1" title={`MeitnerId: ${meitnerSchoolUnit.id}`}>
                {meitnerSchoolUnit.title}
            </div>
            <MultiSelectInput
                items={props.exportSchoolUnits
                    .filter(x => filterExportSchoolUnits(x, meitnerSchoolUnit.id))
                    .map(es => ({ value: es.id, label: es.title ? es.title : '' }))}
                onChange={(_, value) => onChangeFlowSyncSchoolUnit(value, meitnerSchoolUnit)}
                name={meitnerSchoolUnit.title}
                defaultText={t('meitnerExportConfiguration.schoolUnits.selectSchoolUnits')}
                selected={getSelectedFlowSyncSchoolUnit(meitnerSchoolUnit)}
                disabled={props.disabled || meitnerSchoolUnits.code !== StatusCode.COMPLETE || !props.configuration.clientId || !props.configuration.clientSecret}
            />
        </>);
    };

    const disabledShowMeitnerSchoolUnits = () => {
        if (meitnerSchoolUnits.code === StatusCode.ERROR) {
            
            const errorObject = getErrorObject(meitnerSchoolUnits);
            if (errorObject?.error === "MeitnerSchoolUnitError" && (errorObject?.description?.indexOf('Client-ID') !== -1 || errorObject?.description?.indexOf('Client-Secret') !== -1)) {
                return <p><FontAwesomeIcon className="me-2" color='orange' icon={['fas', 'triangle-exclamation']} /> {t('meitnerExportConfiguration.schoolUnits.invalidClientIdOrSecret')}</p>
            }

            if (props.configuration.meitnerSchoolUnits.length)
                return <p>{t('meitnerExportConfiguration.schoolUnits.couldNotFetchSchoolsHasSaved')}</p>
            else
                return <p>{t('meitnerExportConfiguration.schoolUnits.couldNotFetchSchools')}</p>
        }

        if ((!props.configuration.clientId || !props.configuration.clientSecret) && props.configuration.meitnerSchoolUnits.length)
            return <p>{t('meitnerExportConfiguration.schoolUnits.noClientIdOrClientSecretHasSaved')}</p>
    };

    const renderBody = () => {
        if ((!props.configuration.clientId || !props.configuration.clientSecret) && !props.configuration.meitnerSchoolUnits.length) {
            return <p>{t('meitnerExportConfiguration.schoolUnits.noClientIdOrClientSecret')}</p>
        };
        return (<>
            {disabledShowMeitnerSchoolUnits()}
            {renderMeitnerSchoolUnits()}
        </>);
    };

    return (
        <Card className="mb-3">
            <CardHeader tag="h5">
                <Button outline color="secondary" style={{ width: '30px', float: 'right', lineHeight: '0.8', padding: '0.5rem' }} onClick={() => setIsOpen(!isOpen)}>{isOpen ? '-' : '+'}</Button>
                {t('meitnerExportConfiguration.schoolUnits.title')}
            </CardHeader>
            <Collapse isOpen={isOpen}>
                <CardBody>
                    <div>

                        {renderBody()}

                    </div>
                </CardBody>
            </Collapse>
        </Card>
    );
};

export default MeitnerExportSchoolUnits;
